import { useCallback } from 'react';
import { useAppAuth } from '@/lib/useAppAuth';
import { useOpenLogin, useOpenSignup } from '@/store/authModalStore';
import type { SignUpSource } from '@/analytics';

/**
 * THE proactive signup-completion gate — the single client-side counterpart of
 * the server's default-deny 403 SIGNUP_INCOMPLETE (AuthenticationGuard in
 * predictions-api). Every surface that starts a gated action (orders, deposit,
 * KYC entry, …) uses this hook; don't hand-roll the check (see
 * `project/api-contract.md` in predictions-shared).
 *
 * Check it before any other proactive gate (territory, KYC): a registered
 * account that hasn't finished onboarding resumes the signup funnel at its
 * next step (verify email → terms) — not the identity-verification popup,
 * which presumes a fully onboarded user (ARC-145). True guests get the login
 * sheet.
 *
 * Returns a guard: `true` → proceed with the action; `false` → the right auth
 * surface was opened and the action must stop. `source` records the entry
 * point for `sign_up_started` analytics; omit it only where the tracking spec
 * defines no source for the surface.
 */
export function useOnboardingGate(source?: SignUpSource): () => boolean {
  const { isOnboarded, user } = useAppAuth();
  const openLogin = useOpenLogin();
  const openSignup = useOpenSignup();

  return useCallback(() => {
    if (isOnboarded) return true;
    if (user) openSignup(source);
    else openLogin();
    return false;
  }, [isOnboarded, user, openSignup, openLogin, source]);
}
